import Utils from '@/utils/utils'

// - Route templates -------------------

export const FILES_ROUTES = {
    /** Upload a draft file. Returns a backend-owned file URL. */
    upload:   '/spaces/{space}/assistants/{assistant}/files',
    /** List the current user's draft/uploaded files for this assistant. */
    list:     '/spaces/{space}/assistants/{assistant}/files',
    /** Discover the assistant-specific file handling contract. */
    contract: '/spaces/{space}/assistants/{assistant}/files/contract',
    /** Download a stored file or backend-generated artifact by filename. */
    download: '/spaces/{space}/assistants/{assistant}/files/{filename}',
} as const

// - Request / response types -----------------

/** Parameters required to resolve any file route. */
export interface FilesRouteParams {
    base:      string   // Config endpoint, trailing slash stripped
    space:     string   // Space endpoint or space id
    assistant: string   // Assistant endpoint, oldId, or id
    userId:    string
}

/** Response from POST /files — backend-owned URL for the uploaded file. */
export interface FileUploadResponse {
    file_url: string
}

/** Single entry returned by GET /files. */
export interface FileListEntry {
    filename:    string
    file_url:    string
    preview_url?: string
    created_at?: string
    size?:       number
    mime_type?:  string
}

/** Response from GET /files. */
export interface FileListResponse {
    files: FileListEntry[]
}

/** Per-handler entry inside FilesContract.handlers. */
export interface FilesHandler {
    handler_id:            string
    label:                 string
    mime_patterns:         string[]
    extensions:            string[]
    max_size_mb?:          number
    max_width_px?:         number
    max_height_px?:        number
    output_content_type?:  string
    output_extension?:     string
    preview_supported:     boolean
    preview_width_px?:     number
    preview_height_px?:    number
}

/**
 * Full contract returned by GET /files/contract.
 *
 * `enabled` is the master switch — when false the upload UI must be hidden
 * regardless of the assistant config's `upload: true` flag.
 *
 * Top-level `accepted_mime_patterns`, `accepted_extensions`, and
 * `max_files_per_message` are the aggregated values across all handlers
 * and should be used to drive the file picker and validation logic.
 */
export interface FilesContract {
    /** False means file handling is globally disabled for this assistant. */
    enabled:                        boolean
    /** One entry per registered handler (e.g. "image"). */
    handlers?:                      FilesHandler[]
    /** Union of all MIME patterns across handlers, e.g. ["image/*"]. */
    accepted_mime_patterns?:        string[]
    /** Union of all discrete extensions across handlers. */
    accepted_extensions?:           string[]
    /** Maximum files allowed per chat message. */
    max_files_per_message?:         number
    /** Whether cross-assistant file references are permitted. */
    allow_cross_assistant_references?: boolean
}

// - URL builders ---------------------

function buildUrl(
    template: string,
    { base, space, assistant, userId }: FilesRouteParams,
    extra?: Record<string, string>
): string {
    const params = { space, assistant, ...extra }
    const path = Utils.formatEndpoint(template, params)
    const url = `${base}${path}`
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}userID=${encodeURIComponent(userId)}`
}

export function uploadUrl(p: FilesRouteParams): string {
    return buildUrl(FILES_ROUTES.upload, p)
}

export function listUrl(p: FilesRouteParams): string {
    return buildUrl(FILES_ROUTES.list, p)
}

export function contractUrl(p: FilesRouteParams): string {
    return buildUrl(FILES_ROUTES.contract, p)
}

export function downloadUrl(p: FilesRouteParams, filename: string): string {
    return buildUrl(FILES_ROUTES.download, p, { filename })
}

// - Errors ----------------------

/**
 * Thrown by uploadFile() on HTTP errors.
 * `apiMessage` is populated from the response body when the backend provides one
 * (checked under `message`, `error`, and `detail` keys).
 */
export class FileUploadError extends Error {
    readonly status: number
    readonly apiMessage: string | undefined

    constructor(status: number, apiMessage?: string) {
        super(apiMessage ?? `Upload failed: ${status}`)
        this.name = 'FileUploadError'
        this.status = status
        this.apiMessage = apiMessage
    }
}

// - API calls ----------------------

/** Upload a single file. Returns the backend-owned file URL, or throws on failure. */
export async function uploadFile(
    params: FilesRouteParams,
    file: File,
    token: string
): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const res = await fetch(uploadUrl(params), {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
        let apiMessage: string | undefined
        try {
            const body = await res.json()
            apiMessage = body?.message ?? body?.error ?? body?.detail
        } catch { /* ignore parse error */ }
        throw new FileUploadError(res.status, apiMessage)
    }

    const data = await res.json().catch(() => ({}))
    if (!data?.file_url) throw new Error('Upload succeeded but no file_url in response')

    return data as FileUploadResponse
}

/** Fetch the list of files uploaded by this user for this assistant. */
export async function listFiles(
    params: FilesRouteParams,
    token: string
): Promise<FileListResponse> {
    const res = await fetch(listUrl(params), {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) throw new Error(`List files failed: ${res.status}`)

    return res.json() as Promise<FileListResponse>
}

/** Fetch the file handling contract for this assistant. Returns null on failure. */
export async function fetchFilesContract(
    params: FilesRouteParams,
    token: string
): Promise<FilesContract | null> {
    try {
        const res = await fetch(contractUrl(params), {
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return null
        return res.json() as Promise<FilesContract>
    } catch {
        return null
    }
}
